import { useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { InfiniteData, QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import {
  useAddCommunityGroupMember,
  useAddCommunityGroupPendingMember,
  useRemoveCommunityGroupMember,
  useRemoveCommunityGroupPendingMember,
} from '@juno/client-api';
import { CommunityGroup, CommunityUser, PrivacyEnum } from '@juno/client-api/model';
import { MutationAction, onMutation, useGroupMembership, useSettings } from '@juno/utils';

interface GroupJoinButtonProps {
  thisGroup: CommunityGroup | undefined;
  refetchGroup?: () => void;
  refetchMembers?: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<InfiniteData<CommunityUser[], number | undefined>, unknown>>;
}

const GroupJoinButton: React.FC<GroupJoinButtonProps> = ({
  thisGroup,
  refetchGroup,
  refetchMembers,
}) => {
  const [joinLeaveLoading, setJoinLeaveLoading] = useState(false);
  const { siteId, user, refetchMe } = useSettings();

  const addMember = useAddCommunityGroupMember(onMutation(MutationAction.UPDATE, '', () => {}));
  const removeMember = useRemoveCommunityGroupMember(
    onMutation(MutationAction.UPDATE, '', () => {}),
  );
  const addPendingMember = useAddCommunityGroupPendingMember(
    onMutation(MutationAction.UPDATE, '', () => {}),
  );
  const removePendingMember = useRemoveCommunityGroupPendingMember(
    onMutation(MutationAction.UPDATE, '', () => {}),
  );

  const { iAmMember, iAmPendingMember } = useGroupMembership({ thisGroup });

  const handleJoinLeave = async () => {
    setJoinLeaveLoading(true);
    try {
      if (iAmMember) {
        // leave group
        await removeMember.mutateAsync({
          siteId,
          groupId: thisGroup?.id || '',
          userId: user?.id || '',
        });
      } else if (iAmPendingMember) {
        // revoke join request
        await removePendingMember.mutateAsync({
          siteId,
          groupId: thisGroup?.id || '',
          userId: user?.id || '',
        });
      } else if (thisGroup?.privacy === PrivacyEnum.public) {
        // join group
        await addMember.mutateAsync({
          siteId,
          groupId: thisGroup?.id || '',
          data: { users: [user?.id || ''] },
        });
      } else if (thisGroup?.privacy === PrivacyEnum.private) {
        // request to join group
        await addPendingMember.mutateAsync({
          siteId,
          groupId: thisGroup?.id || '',
          data: { users: [user?.id || ''] },
        });
      }
      refetchMe && refetchMe();
      refetchGroup && (await refetchGroup());
      setJoinLeaveLoading(false);
    } catch (e) {
      console.error(e);
      setJoinLeaveLoading(false);
    }
  };

  const buttonText = useMemo(() => {
    if (iAmMember) return `Leave ${thisGroup?.title}`;
    if (iAmPendingMember) return 'Cancel Join Request';
    if (thisGroup?.privacy === PrivacyEnum.public) return `Join ${thisGroup?.title}`;
    if (thisGroup?.privacy === PrivacyEnum.private) return `Request to Join ${thisGroup?.title}`;
    if (thisGroup?.privacy === PrivacyEnum.secret) return `Join ${thisGroup?.title}`;
    return '';
  }, [iAmMember, iAmPendingMember, thisGroup?.privacy]);

  return (
    <>
      {thisGroup && thisGroup.allow_join_leave && (
        <LoadingButton
          variant={iAmMember ? 'outlined' : 'contained'}
          size='large'
          sx={{
            maxHeight: 36,
            width: '100%',
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: '0.21px',
          }}
          onClick={async (e) => {
            await handleJoinLeave();
            refetchMembers && refetchMembers();
          }}
          loading={joinLeaveLoading}
          disabled={
            thisGroup === undefined || (thisGroup?.privacy === PrivacyEnum.secret && !iAmMember)
          }
          aria-label={'Join or Leave Group'}
        >
          {buttonText}
        </LoadingButton>
      )}
    </>
  );
};

export default GroupJoinButton;
